import React, { useEffect, useState } from "react";
import { PartyStore } from "@store/PartyStore.js";
import { InventoryStore } from "@store/InventoryStore.js";
import { EventBus } from "@engine/EventBus.js";
import {
  totalInventoryWeight,
  carryCapacity,
  addItem,
  removeItem,
} from "@engine/inventory/CharacterInventory.js";
import { equipItem, unequipSlot } from "@engine/party/equipment.js";
// Aliased import: the react-hooks lint rule pattern-matches `use*` names.
import { useConsumable as consumeItem, transferItem } from "@engine/party/consumables.js";
import { MemberPane } from "./CharacterSheet.js";
import { ItemIcon } from "./ItemIcon.js";
import type { PlayerCharacter, Character } from "@engine/types/entity.js";
import type { Armor, EquipmentSlot, Item, ItemStack, Weapon } from "@engine/types/items.js";
import { currencyAbbrev } from "@engine/adventure/ActiveAdventure.js";
import { useLayout } from "../hooks/useLayout.js";

// ── Slot layout ────────────────────────────────────────────────────────────────

const SLOT_LABELS: Record<EquipmentSlot, string> = {
  head: "HEAD",
  necklace: "NECK",
  torso: "TORSO",
  gloves: "GLOVES",
  ring1: "RING 1",
  ring2: "RING 2",
  boots: "BOOTS",
  mainHand: "MAIN",
  offHand: "OFF",
};

/** Render order for the equipment table. */
const SLOT_ORDER: readonly EquipmentSlot[] = [
  "mainHand",
  "offHand",
  "head",
  "torso",
  "gloves",
  "boots",
  "necklace",
  "ring1",
  "ring2",
];

// ── Portrait fallback ─────────────────────────────────────────────────────────

function silhouettePath(characterClass: string): string {
  const KNOWN = ["fighter", "wizard", "rogue", "cleric", "ranger", "paladin", "bard", "druid", "monk", "sorcerer", "warlock", "barbarian"];
  const slug = characterClass.toLowerCase();
  return `/engine/portraits/silhouettes/${KNOWN.includes(slug) ? slug : "adventurer"}.svg`;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function WeightBar({ current, max }: { current: number; max: number }) {
  const pct = max > 0 ? Math.min(100, (current / max) * 100) : 0;
  const color = pct > 95 ? "#f44336" : pct > 75 ? "#ff9800" : "#4caf50";
  return (
    <div>
      <div
        style={{
          background: "#333",
          borderRadius: 3,
          height: 8,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: color,
            transition: "width 0.2s",
          }}
        />
      </div>
      <div style={{ color: "#aaa", fontSize: 10, fontFamily: "monospace", marginTop: 3 }}>
        {current.toFixed(1)} / {max} lbs
      </div>
    </div>
  );
}

// ── Selection state type ──────────────────────────────────────────────────────

type Selection =
  | { source: "carried"; itemId: string }
  | { source: EquipmentSlot; itemId: string }
  | null;

// ── Equip helpers ─────────────────────────────────────────────────────────────

function canEquipInSlot(item: Item, slot: EquipmentSlot): boolean {
  if (item.slot === undefined) return false;
  // Both hand slots are interchangeable for weapons/spellbooks
  const handSlots = new Set<EquipmentSlot>(["mainHand", "offHand"]);
  if (handSlots.has(item.slot) && handSlots.has(slot)) return true;
  return item.slot === slot;
}

// ── Item detail panel (U-series: inspect stats before equipping) ──────────────

function abilityMod(score: number): number {
  return Math.floor((score - 10) / 2);
}

function fmtMod(n: number): string {
  return n >= 0 ? `+${String(n)}` : String(n);
}

/** Resolve the currently selected item on `member`, if it still exists. */
function resolveSelection(member: PlayerCharacter, selection: Selection): Item | null {
  if (!selection) return null;
  const inventory = InventoryStore.get(member.instanceId);
  if (selection.source === "carried") {
    return inventory.stacks.find((s) => s.item.id === selection.itemId)?.item ?? null;
  }
  const equipped = inventory.equipment[selection.source];
  return equipped && equipped.id === selection.itemId ? equipped : null;
}

const DETAIL_LABEL: React.CSSProperties = {
  color: "#8899bb",
  fontSize: 10,
  letterSpacing: 1,
  textTransform: "uppercase",
  marginRight: 6,
};

const ACTION_BTN: React.CSSProperties = {
  padding: "8px 14px",
  fontFamily: "monospace",
  fontSize: 12,
  background: "#2a4060",
  color: "#c8e6ff",
  border: "1px solid rgba(100,160,220,0.6)",
  borderRadius: 3,
  cursor: "pointer",
};

function ItemDetailPanel({
  member,
  members,
  selection,
  onSelect,
  fullscreen,
}: {
  member: PlayerCharacter;
  members: readonly PlayerCharacter[];
  selection: Selection;
  onSelect: (sel: Selection) => void;
  fullscreen: boolean;
}) {
  const [notice, setNotice] = useState<string | null>(null);
  const item = resolveSelection(member, selection);
  if (!item) return null;

  const lines: React.ReactNode[] = [];

  if (item.category === "weapon") {
    const w = item as Weapon;
    lines.push(
      <span key="dmg">
        <span style={DETAIL_LABEL}>Damage</span>
        {w.damage.count}d{w.damage.sides} {w.damageType} · uses {w.abilityScore}
        {w.properties.length > 0 && ` · ${w.properties.join(", ")}`}
      </span>,
    );
    // Who swings it best: rank the party by the weapon's attack ability.
    const ranked = [...members]
      .map((m) => ({ name: m.name, mod: abilityMod(m.abilities[w.abilityScore]) }))
      .sort((a, b) => b.mod - a.mod);
    const best = ranked[0];
    const worst = ranked.length > 1 ? ranked[ranked.length - 1] : undefined;
    if (best) {
      lines.push(
        <span key="best">
          <span style={DETAIL_LABEL}>Best wielder</span>
          {best.name} ({w.abilityScore} {fmtMod(best.mod)})
          {worst && ` · worst: ${worst.name} (${fmtMod(worst.mod)})`}
        </span>,
      );
    }
  } else if (item.category === "armor") {
    const a = item as Armor;
    const dex = a.addDex
      ? a.maxDexBonus === null
        ? " + DEX"
        : ` + DEX (max ${String(a.maxDexBonus)})`
      : "";
    lines.push(
      <span key="ac">
        <span style={DETAIL_LABEL}>AC</span>
        {a.baseAC}
        {dex}
        {a.stealthDisadvantage && " · stealth disadvantage"}
        {a.strengthRequirement > 0 && ` · requires STR ${String(a.strengthRequirement)}`}
      </span>,
    );
  }

  const value = Math.floor(item.valueCp / 100);
  lines.push(
    <span key="misc">
      <span style={DETAIL_LABEL}>Carry</span>
      {item.weightLbs} lb · {value} {currencyAbbrev()}
      {item.slot !== undefined && ` · slot: ${SLOT_LABELS[item.slot]}`}
    </span>,
  );

  // ── Actions ────────────────────────────────────────────────────────────
  const carried = selection?.source === "carried";
  const isHandItem = item.slot === "mainHand" || item.slot === "offHand";
  const others = members.filter((m) => m.instanceId !== member.instanceId);

  const actions: React.ReactNode[] = [];
  if (carried && item.slot !== undefined) {
    if (isHandItem) {
      actions.push(
        <button key="eq-main" type="button" style={ACTION_BTN} onClick={() => {
          doEquip(member, selection, "mainHand");
          onSelect(null);
        }}>
          Equip main
        </button>,
        <button key="eq-off" type="button" style={ACTION_BTN} onClick={() => {
          doEquip(member, selection, "offHand");
          onSelect(null);
        }}>
          Equip off
        </button>,
      );
    } else {
      const targetSlot = item.slot;
      actions.push(
        <button key="eq" type="button" style={ACTION_BTN} onClick={() => {
          doEquip(member, selection, targetSlot);
          onSelect(null);
        }}>
          Equip
        </button>,
      );
    }
  }
  if (!carried && selection) {
    const slot = selection.source as EquipmentSlot;
    actions.push(
      <button key="uneq" type="button" style={ACTION_BTN} onClick={() => {
        doUnequip(member, slot);
        onSelect(null);
      }}>
        Unequip
      </button>,
    );
  }
  if (carried && item.consumable) {
    actions.push(
      <button key="use" type="button" style={{ ...ACTION_BTN, background: "#2a5040", borderColor: "rgba(120,220,160,0.6)" }} onClick={() => {
        consumeItem(member.instanceId, item);
        setNotice(null);
      }}>
        Use
      </button>,
    );
  }
  if (carried) {
    for (const other of others) {
      actions.push(
        <button key={`give-${other.instanceId}`} type="button" style={{ ...ACTION_BTN, background: "#3a3050" }} onClick={() => {
          const r = transferItem(member.instanceId, other.instanceId, item);
          setNotice(
            r === "ok"
              ? `Gave ${item.name} to ${other.name}.`
              : r === "no_capacity"
                ? `${other.name} can't carry any more.`
                : "Item is gone.",
          );
        }}>
          → {other.name.split(" ")[0]}
        </button>,
      );
    }
  }
  actions.push(
    <button key="close" type="button" style={{ ...ACTION_BTN, background: "rgba(0,0,0,0.4)", color: "#aaa", borderColor: "rgba(255,255,255,0.2)" }} onClick={() => {
      onSelect(null);
    }}>
      Close
    </button>,
  );

  const panel = (
    <div
      data-testid="item-detail-panel"
      style={{
        marginTop: fullscreen ? 0 : 10,
        padding: fullscreen ? 16 : "8px 12px",
        background: fullscreen ? "#101828" : "rgba(20,30,50,0.6)",
        border: "1px solid rgba(100,160,220,0.35)",
        borderRadius: 4,
        fontFamily: "monospace",
        fontSize: 12,
        color: "#c8e6ff",
        display: "flex",
        flexDirection: "column",
        gap: fullscreen ? 10 : 4,
        ...(fullscreen ? { width: "min(92vw, 420px)", maxHeight: "84vh", overflowY: "auto" } : null),
      }}
    >
      <span style={{ color: "#ffd0a0", fontSize: fullscreen ? 16 : 13 }}>
        {item.name}
        <span style={{ color: "#667", fontSize: 10, marginLeft: 8, textTransform: "uppercase" }}>
          {item.category}
        </span>
      </span>
      {lines}
      {item.description && (
        <span style={{ color: "#99a", fontStyle: "italic", fontSize: 11 }}>{item.description}</span>
      )}
      {notice && <span style={{ color: "#ffd0a0", fontSize: 11 }}>{notice}</span>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>{actions}</div>
    </div>
  );

  if (!fullscreen) return panel;
  // Mobile: the panel takes over the screen so every action has room.
  return (
    <div
      data-testid="item-detail-modal"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 300,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "auto",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onSelect(null);
      }}
    >
      {panel}
    </div>
  );
}

function doEquip(
  member: PlayerCharacter,
  sel: Selection,
  targetSlot: EquipmentSlot,
): void {
  if (!sel) return;
  const inv = InventoryStore.get(member.instanceId);

  if (sel.source === "carried") {
    const stack = inv.stacks.find((s) => s.item.id === sel.itemId);
    if (!stack) return;
    if (!canEquipInSlot(stack.item, targetSlot)) return;
    const { inventory: withRemoved } = removeItem(inv, sel.itemId, 1);
    const { record: newEq, displaced } = equipItem(
      stack.item,
      targetSlot,
      withRemoved.equipment,
    );
    let finalInv = { ...withRemoved, equipment: newEq };
    for (const d of displaced) {
      const { inventory: next } = addItem(finalInv, d, 1, member.abilities.STR);
      finalInv = next;
    }
    InventoryStore.set(member.instanceId, finalInv);
  } else {
    // Moving from one equipment slot to another
    const srcItem = inv.equipment[sel.source];
    if (!srcItem) return;
    if (sel.source === targetSlot) return; // no-op
    if (!canEquipInSlot(srcItem, targetSlot)) return;
    const { record: withUnequipped } = unequipSlot(sel.source, inv.equipment);
    const { record: newEq, displaced } = equipItem(srcItem, targetSlot, withUnequipped);
    let finalInv = { ...inv, equipment: newEq };
    for (const d of displaced) {
      const { inventory: next } = addItem(finalInv, d, 1, member.abilities.STR);
      finalInv = next;
    }
    InventoryStore.set(member.instanceId, finalInv);
  }
}

function doUnequip(member: PlayerCharacter, slot: EquipmentSlot): void {
  const inv = InventoryStore.get(member.instanceId);
  const { record: newEq, item } = unequipSlot(slot, inv.equipment);
  if (!item) return;
  const { inventory: next } = addItem(
    { ...inv, equipment: newEq },
    item,
    1,
    member.abilities.STR,
  );
  InventoryStore.set(member.instanceId, next);
}

// ── Slot button ────────────────────────────────────────────────────────────────

interface SlotRowProps {
  slot: EquipmentSlot;
  member: PlayerCharacter;
  selection: Selection;
  onSelect: (sel: Selection) => void;
}

function SlotRow({ slot, member, selection, onSelect }: SlotRowProps) {
  const eq = InventoryStore.get(member.instanceId).equipment;
  const item = eq[slot] ?? null;

  const isSelected = selection?.source === slot;
  const carriedSel =
    selection?.source === "carried"
      ? inv(member).stacks.find((s) => s.item.id === selection.itemId)?.item
      : undefined;
  const isTarget = carriedSel !== undefined && canEquipInSlot(carriedSel, slot);

  const handleClick = () => {
    if (selection?.source === "carried") {
      doEquip(member, selection, slot);
      onSelect(null);
    } else if (selection?.source === slot) {
      onSelect(null);
    } else if (item) {
      onSelect({ source: slot, itemId: item.id });
    } else if (selection !== null) {
      doEquip(member, selection, slot);
      onSelect(null);
    } else {
      onSelect(null);
    }
  };

  const bg = isSelected
    ? "rgba(120,60,30,0.7)"
    : isTarget
      ? "rgba(30,80,50,0.5)"
      : "transparent";
  const labelColor = isSelected ? "#ffd0a0" : "#888";
  const itemColor = item ? (isSelected ? "#ffd0a0" : "#c8e6ff") : "#555";

  return (
    <tr
      data-testid={`equip-slot-${slot}-${member.instanceId}`}
      onClick={handleClick}
      style={{
        cursor: "pointer",
        background: bg,
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}
      title={item ? item.name : `Empty ${SLOT_LABELS[slot]} slot`}
    >
      <td
        style={{
          padding: "5px 8px",
          color: labelColor,
          fontFamily: "monospace",
          fontSize: 10,
          letterSpacing: 1,
          width: 70,
          whiteSpace: "nowrap",
        }}
      >
        {SLOT_LABELS[slot]}
      </td>
      <td
        style={{
          padding: "5px 8px",
          color: itemColor,
          fontFamily: "monospace",
          fontSize: 11,
        }}
      >
        {item ? (
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <ItemIcon itemId={item.id} name={item.name} size={20} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {item.name}
            </span>
          </span>
        ) : (
          <span style={{ color: "#444" }}>—</span>
        )}
      </td>
    </tr>
  );
}

function inv(member: PlayerCharacter) {
  return InventoryStore.get(member.instanceId);
}

function EquipmentTable({
  member,
  selection,
  onSelect,
}: {
  member: PlayerCharacter;
  selection: Selection;
  onSelect: (sel: Selection) => void;
}) {
  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        tableLayout: "fixed",
      }}
    >
      <tbody>
        {SLOT_ORDER.map((slot) => (
          <SlotRow
            key={slot}
            slot={slot}
            member={member}
            selection={selection}
            onSelect={onSelect}
          />
        ))}
      </tbody>
    </table>
  );
}

function ItemList({
  member,
  selection,
  onSelect,
}: {
  member: PlayerCharacter;
  selection: Selection;
  onSelect: (sel: Selection) => void;
}) {
  const inventory = InventoryStore.get(member.instanceId);
  const stacks: readonly ItemStack[] = inventory.stacks;
  const weight = totalInventoryWeight(inventory);
  const capacity = carryCapacity(member.abilities.STR);

  const handleCarriedClick = (stack: ItemStack) => {
    if (selection?.source !== "carried" && selection !== null) {
      // Selected equipped item — unequip it + put in carried
      doUnequip(member, selection.source as EquipmentSlot);
      onSelect(null);
    } else if (selection?.itemId === stack.item.id) {
      onSelect(null); // deselect
    } else {
      onSelect({ source: "carried", itemId: stack.item.id });
    }
  };

  const handleListAreaClick = () => {
    if (selection && selection.source !== "carried") {
      // Unequip back to carried
      doUnequip(member, selection.source as EquipmentSlot);
      onSelect(null);
    }
  };

  return (
    <div
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
      onClick={handleListAreaClick}
    >
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          fontFamily: "monospace",
          fontSize: 11,
        }}
      >
        {stacks.length === 0 && (
          <div style={{ color: "#555", padding: 8 }}>Nothing carried.</div>
        )}
        {stacks.map((stack) => {
          const isSelected =
            selection?.source === "carried" && selection.itemId === stack.item.id;
          return (
            <div
              key={stack.item.id}
              data-testid={`inv-stack-${stack.item.id}`}
              onClick={(e) => {
                e.stopPropagation();
                handleCarriedClick(stack);
              }}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "3px 6px",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
                color: isSelected ? "#ffd0a0" : "#ddd",
                background: isSelected ? "rgba(120,60,30,0.4)" : "transparent",
                cursor: "pointer",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                <ItemIcon itemId={stack.item.id} name={stack.item.name} size={20} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {stack.item.name}
                  {stack.quantity > 1 && (
                    <span style={{ color: "#888", marginLeft: 4 }}>×{stack.quantity}</span>
                  )}
                </span>
              </span>
              <span style={{ color: "#666", marginLeft: 8, flexShrink: 0 }}>
                {(stack.item.weightLbs * stack.quantity).toFixed(1)} lb
              </span>
            </div>
          );
        })}
      </div>
      <div style={{ padding: "6px 0 0" }}>
        <WeightBar current={weight} max={capacity} />
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface Props {
  onBack: () => void;
  initialMemberId?: string;
}

type Tab = "gear" | "stats";

export function InventoryScreen({ onBack, initialMemberId }: Props) {
  const [members, setMembers] = useState<readonly PlayerCharacter[]>(() =>
    PartyStore.getMembers(),
  );
  const [activeIdx, setActiveIdx] = useState(() => {
    if (!initialMemberId) return 0;
    const idx = PartyStore.getMembers().findIndex(
      (m) => m.instanceId === initialMemberId,
    );
    return idx >= 0 ? idx : 0;
  });
  const [tab, setTab] = useState<Tab>("gear");
  const [selection, setSelection] = useState<Selection>(null);
  const layoutMode = useLayout();
  const stackVertical = layoutMode === "mobile-portrait";

  useEffect(() => {
    const refresh = () => setMembers([...PartyStore.getMembers()]);
    const unsub = EventBus.on("ui:party_update", refresh);
    return unsub;
  }, []);

  const member = members[activeIdx] ?? members[0];
  if (!member) {
    return (
      <div style={overlayStyle}>
        <div style={panelStyle}>
          <div style={{ color: "#aaa", fontFamily: "monospace" }}>No party members.</div>
          <BackButton onClick={onBack} />
        </div>
      </div>
    );
  }

  const tabBtn = (t: Tab, label: string) => (
    <button
      key={t}
      type="button"
      onClick={() => setTab(t)}
      style={{
        padding: "4px 16px",
        fontFamily: "monospace",
        fontSize: 12,
        background: tab === t ? "#2a4060" : "rgba(0,0,0,0.3)",
        color: tab === t ? "#c8e6ff" : "#888",
        border: `1px solid ${tab === t ? "rgba(100,160,220,0.6)" : "rgba(255,255,255,0.1)"}`,
        borderRadius: 3,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={overlayStyle}>
      <div
        style={{
          ...panelStyle,
          ...(stackVertical ? { padding: 12, width: "100vw", maxHeight: "100vh", borderRadius: 0 } : null),
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h2
            style={{
              margin: 0,
              color: "white",
              fontFamily: "monospace",
              fontSize: 18,
              letterSpacing: 2,
            }}
          >
            INVENTORY — {member.name.toUpperCase()}
          </h2>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {tabBtn("gear", "GEAR")}
            {tabBtn("stats", "STATS")}
            <BackButton onClick={onBack} />
          </div>
        </div>

        {/* Content */}
        {tab === "stats" ? (
          <MemberPane member={member} />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: stackVertical ? "column" : "row",
              gap: 12,
              flex: 1,
              minHeight: 0,
              overflow: stackVertical ? "auto" : "visible",
            }}
          >
            {/* Equipment table — fixed-width column on desktop, full-width on mobile portrait */}
            <div
              style={{
                flex: stackVertical ? "0 0 auto" : "0 0 280px",
                background: "rgba(0,0,0,0.3)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 4,
                padding: 10,
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  color: "#aaa",
                  fontFamily: "monospace",
                  fontSize: 10,
                  marginBottom: 6,
                  letterSpacing: 1,
                }}
              >
                EQUIPPED
              </div>
              <EquipmentTable member={member} selection={selection} onSelect={setSelection} />
            </div>
            {/* Items panel — fills the rest on desktop, stacks below the doll on mobile */}
            <div
              style={{
                flex: stackVertical ? "0 0 auto" : 1,
                display: "flex",
                flexDirection: "column",
                background: "rgba(0,0,0,0.3)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 4,
                padding: 10,
                minHeight: stackVertical ? 180 : 0,
              }}
            >
              <div
                style={{
                  color: "#aaa",
                  fontFamily: "monospace",
                  fontSize: 10,
                  marginBottom: 6,
                  letterSpacing: 1,
                }}
              >
                CARRIED ITEMS
              </div>
              <ItemList member={member} selection={selection} onSelect={setSelection} />
            </div>
          </div>
        )}

        {/* Selected-item stats + actions — inline under the gear panes on
            desktop, full-screen takeover on mobile portrait (small screens
            need room for the equip/use/give buttons) */}
        {tab === "gear" && (
          <ItemDetailPanel
            member={member}
            members={members}
            selection={selection}
            onSelect={setSelection}
            fullscreen={stackVertical}
          />
        )}

        {/* Headshot strip — character navigation */}
        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 12,
            justifyContent: "center",
          }}
        >
          {members.map((m, i) => (
            <button
              key={m.instanceId}
              type="button"
              data-testid={`inv-headshot-${m.instanceId}`}
              onClick={() => { setActiveIdx(i); setSelection(null); }}
              title={m.name}
              style={{
                width: 56,
                height: 56,
                padding: 0,
                border: `2px solid ${i === activeIdx ? "#7dcfff" : "rgba(255,255,255,0.2)"}`,
                borderRadius: 4,
                background: "#c9c1b0",
                cursor: "pointer",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <img
                src={(m as Partial<Character>).portrait ?? silhouettePath(m.characterClass)}
                alt={m.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = silhouettePath("adventurer");
                }}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "4px 12px",
        fontFamily: "monospace",
        fontSize: 12,
        background: "rgba(0,0,0,0.4)",
        color: "#aaa",
        border: "1px solid rgba(255,255,255,0.2)",
        borderRadius: 3,
        cursor: "pointer",
      }}
    >
      Back
    </button>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  background: "rgba(0,0,0,0.85)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 300,
  pointerEvents: "auto",
};

const panelStyle: React.CSSProperties = {
  background: "#111",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 8,
  padding: 24,
  width: "min(95vw, 860px)",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};
